import { createSlice } from '@reduxjs/toolkit';
import type { AgentBlockStoreState } from '../../types/negotiationAgents';
import {
  createFlowCustomGroup,
  createFlowCustomGroupInstance,
  deleteFlowCustomGroup,
  deleteFlowCustomGroupInstance,
  listFlowCustomGroupInstances,
  listFlowCustomGroups,
  updateFlowCustomGroup,
  updateFlowCustomGroupInstance,
} from './actions';

const initialState: AgentBlockStoreState = {
  groups: [],
  instancesByFlow: {},
  instancesPagination: {},
  groupsPagination: { page: 1, total: 0, hasMore: false, status: 'idle' },
  loading: false,
  error: null,
};

const flowCustomGroupsSlice = createSlice({
  name: 'flowCustomGroups',
  initialState,
  reducers: {
    clearFlowCustomGroupsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listFlowCustomGroups.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.groupsPagination.status = 'loading';
        state.groupsPagination.requestId = action.meta.requestId;
      })
      .addCase(listFlowCustomGroups.fulfilled, (state, action) => {
        if (state.groupsPagination.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.groups = action.payload.items;
        const { page, total, hasMore } = action.payload;
        state.groupsPagination = {
          page,
          total,
          hasMore,
          status: 'success',
          requestId: action.meta.requestId,
        };
      })
      .addCase(listFlowCustomGroups.rejected, (state, action) => {
        if (state.groupsPagination.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.groupsPagination.status = 'error';
        state.error = action.error.message || 'Erro ao carregar grupos';
      })
      .addCase(createFlowCustomGroup.fulfilled, (state, action) => {
        state.groups = [action.payload, ...state.groups];
      })
      .addCase(updateFlowCustomGroup.fulfilled, (state, action) => {
        const index = state.groups.findIndex((group) => group.id === action.payload.id);
        if (index !== -1) state.groups[index] = action.payload;
      })
      .addCase(deleteFlowCustomGroup.fulfilled, (state, action) => {
        state.groups = state.groups.filter((group) => group.id !== action.payload);
      })
      .addCase(listFlowCustomGroupInstances.pending, (state, action) => {
        const input = action.meta.arg;
        const flowId = typeof input === 'string' ? input : input.flowId;
        const previous = state.instancesPagination[flowId];
        state.instancesPagination[flowId] = {
          page: previous?.page ?? 1,
          total: previous?.total ?? 0,
          hasMore: previous?.hasMore ?? false,
          status: 'loading',
          requestId: action.meta.requestId,
        };
      })
      .addCase(listFlowCustomGroupInstances.fulfilled, (state, action) => {
        const { flowId, page } = action.payload;
        if (state.instancesPagination[flowId]?.requestId !== action.meta.requestId) return;
        const previous = page.page > 1 ? (state.instancesByFlow[flowId] ?? []) : [];
        const byId = new Map(previous.map((item) => [item.id, item]));
        page.items.forEach((item) => byId.set(item.id, item));
        state.instancesByFlow[flowId] = [...byId.values()];
        state.instancesPagination[flowId] = {
          page: page.page,
          total: page.total,
          hasMore: page.hasMore,
          status: 'success',
          requestId: action.meta.requestId,
        };
      })
      .addCase(listFlowCustomGroupInstances.rejected, (state, action) => {
        const input = action.meta.arg;
        const flowId = typeof input === 'string' ? input : input.flowId;
        if (state.instancesPagination[flowId]?.requestId === action.meta.requestId)
          state.instancesPagination[flowId].status = 'error';
      })
      .addCase(createFlowCustomGroupInstance.fulfilled, (state, action) => {
        const flowId = action.payload.flowId;
        const current = state.instancesByFlow[flowId] || [];
        state.instancesByFlow[flowId] = [action.payload, ...current];
      })
      .addCase(updateFlowCustomGroupInstance.fulfilled, (state, action) => {
        const flowId = action.payload.flowId;
        const current = state.instancesByFlow[flowId] || [];
        state.instancesByFlow[flowId] = current.map((instance) =>
          instance.id === action.payload.id ? action.payload : instance,
        );
      })
      .addCase(deleteFlowCustomGroupInstance.fulfilled, (state, action) => {
        const current = state.instancesByFlow[action.payload.flowId] || [];
        state.instancesByFlow[action.payload.flowId] = current.filter(
          (instance) => instance.id !== action.payload.instanceId,
        );
      });
  },
});

export default flowCustomGroupsSlice.reducer;
